import React, { useEffect, useState } from "react";
import { PatternFormat } from "react-number-format";
import { CreditState } from "../../stores/credit/credit-state.interface";
import { UserInterface } from "../../interfaces/credit/user.interface";
import { isAfter, isBefore, isValid, parse, startOfDay, subYears } from "date-fns";
import { UserService } from "../../services/user.service";
import { transliterate } from "../../utils";
import { useCreditSelector } from "../../stores/product/product.store";

interface UserFormProps {
    onSubmit: (user: UserInterface) => void;
}

export const UserForm: React.FC<UserFormProps> = ({ onSubmit }) => {
    const user = useCreditSelector((state: CreditState) => state.user);
    const translateText = useCreditSelector((state: CreditState) => state.text);

    const [firstName, setFirstName] = useState(user.firstName);
    const [lastName, setLastName] = useState(user.lastName);
    const [idno, setIdno] = useState(user.idno);
    const [phone, setPhone] = useState(user.phone);
    const [email, setEmail] = useState(user.email);
    const [isValidEmail, setIsValidEmail] = useState(Boolean(user.email));
    const [birthday, setBirthday] = useState(user.birthday);
    const [commentary, setCommentary] = useState(user.commentary);
    const [disabled, setDisabled] = useState<boolean>(true);

    const [emailError, setEmailError] = useState('');

    const validateDate = (birthday: string) => {
        const parsedDate = parse(birthday, 'yyyy-MM-dd', new Date());
        if (!isValid(parsedDate)) return false;

        const today = startOfDay(new Date());
        const beforeDay = subYears(new Date(), 100);
        return isAfter(today, parsedDate) && isBefore(beforeDay, parsedDate);
    }

    useEffect(() => {
        const isValid = Boolean(firstName.trim()) &&
            Boolean(lastName.trim()) &&
            idno.length === 13 &&
            validateDate(birthday) &&
            isValidEmail &&
            phone.replaceAll(' ', '').length === 12;
        setDisabled(!isValid);
    }, [birthday, firstName, idno, isValidEmail, lastName, phone]);

    useEffect(() => {
        setIsValidEmail(Boolean(email.length) && email === user.email);
    }, [email, user.email]);

    const changeEmail = (value: string) => {
        setEmail(value);
        if(!value.trim()) {
            setIsValidEmail(false);
        } else {
            const regex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
            if(regex.test(value)) {
                setEmailError('');
                UserService.validEmail(value).then(() => setIsValidEmail(true)).catch((e) => {
                    setIsValidEmail(false);
                    setEmailError(e.response.data.message);
                });
            }
        }
    }

    const checkFirstDigit = (inputValue: string) => {
        const digits = inputValue.replace(/\D/g, '');
        return digits.length === 0 || digits[0] === '6' || digits[0] === '7';
    };

    const handleTransliterate = (e: React.ChangeEvent<HTMLInputElement>, callback: any = null) => {
        const translit = transliterate(e.target.value);
        if(callback) {
            callback(translit);
        }
    }

    const submit = () => {
        if(!email.trim()) {
            setIsValidEmail(false);
            return ;
        }
        onSubmit({
            firstName,
            lastName,
            idno,
            phone,
            email,
            birthday,
            commentary
        });
    }

    return (
        <div className="bg-white rounded-2xl p-5">
            <div className="w-full mb-6">
                <label htmlFor="firstName"
                       className="text-[14px] font-medium mb-3 block text-black">{ translateText.firstName }<span
                    className="text-red-500"> *</span></label>
                <input id="firstName"
                       type="text"
                       className="p-4 bg-light-gray w-full h-14 box-border border-none caret-yellow rounded-xl shadow-none"
                       value={ firstName }
                       onChange={ (e) => handleTransliterate(e, setFirstName) }/>
            </div>
            <div className="w-full mb-6">
                <label htmlFor="lastName"
                       className="text-[14px] font-medium mb-3 block text-black">{ translateText.lastName }<span
                    className="text-red-500"> *</span></label>
                <input id="lastName"
                       type="text"
                       className="p-4 bg-light-gray w-full h-14 box-border border-none caret-yellow rounded-xl shadow-none"
                       value={ lastName }
                       onChange={ (e) => handleTransliterate(e, setLastName) }/>
            </div>
            <div className="w-full mb-6">
                <label htmlFor="idno"
                       className="text-[14px] font-medium mb-3 block text-black">{ translateText.idno }<span
                    className="text-red-500"> *</span></label>
                <PatternFormat
                    id="idno"
                    format="#############"
                    value={ idno }
                    onChange={ (e) => setIdno(e.target.value) }
                    type="text"
                    className="p-4 bg-light-gray w-full h-14 box-border border-none caret-yellow rounded-xl font-medium shadow-none"
                />
            </div>
            <div className="w-full mb-5">
                <label htmlFor="phone"
                       className="text-[14px] font-medium mb-3 block text-black">{ translateText.phoneNumber }<span
                    className="text-red-500"> *</span></label>
                <PatternFormat
                    id="phone"
                    placeholder="Введите ваш телефон"
                    value={ phone }
                    onChange={ (e) => setPhone(e.target.value) }
                    format="+373 ########"
                    type="tel"
                    isAllowed={ ({ value }) => checkFirstDigit(value) }
                    allowEmptyFormatting
                    className="p-4 bg-light-gray w-full h-14 box-border border-none caret-yellow rounded-xl font-medium shadow-none"
                />
            </div>
            <div className="w-full mb-5">
                <label htmlFor="birthday"
                       className="text-[14px] font-medium mb-3 block text-black">{ translateText.birthday }<span
                    className="text-red-500"> *</span></label>
                <input id="birthday"
                       type="date"
                       className="outline-none p-4 bg-light-gray w-full h-14 box-border border-none caret-yellow rounded-xl shadow-none"
                       value={ birthday }
                       onChange={ (e) => setBirthday(e.target.value) }/>
            </div>
            <div className="w-full mb-5">
                <label htmlFor="email"
                       className="text-[14px] font-medium mb-3 block text-black">{ translateText.email }<span
                    className="text-red-500"> *</span></label>
                <input id="email"
                       type="email"
                       className="outline-none p-4 bg-light-gray w-full h-14 box-border border-none caret-yellow rounded-xl shadow-none"
                       value={ email }
                       onChange={ (e) => changeEmail(e.target.value) }
                       // onBlur={ (e) => onBlurEmail(e.target.value) }
                />
                { emailError && <p className="text-red-500 mt-1 text-sm">{ emailError }</p> }
            </div>
            <div className="w-full mb-5">
                <label htmlFor="commentary"
                       className="text-[14px] font-medium mb-3 block text-black">{ translateText.commentary }</label>
                <textarea name="commentary" rows={ 10 }
                          className="outline-none p-4 bg-light-gray w-full h-14 box-border border-none caret-yellow rounded-xl shadow-none"
                          onChange={ (e) => setCommentary(e.target.value) }
                          value={ commentary }></textarea>
            </div>
            <button
                disabled={ disabled }
                onClick={ () => submit() }
                className={ `font-semibold w-full rounded-xl ${ disabled ? 'bg-light-dark-gray text-white' : 'bg-yellow text-dark-gray' } h-14` }
            >{ translateText.continue }
            </button>
        </div>
    )
}